import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Loader,
  Alert,
  ActionIcon,
} from '@vipso/shared';
import { IconChevronLeft, IconChevronRight, IconDownload, IconX } from '@tabler/icons-react';
import { fetchFileBlob, parseFileIdFromUrl } from '../../api/fetchFileBlob';
import type { RootState } from '../../store';

export type FileViewerOpenArgs = {
  fileId?: string | number;
  url?: string;
  fileName?: string;
  contentTypeHint?: string;
  /** Sibling file ids for prev/next (e.g. product gallery). */
  siblings?: Array<string | number>;
  siblingIndex?: number;
};

type FileViewerContextValue = {
  openFileViewer: (args: FileViewerOpenArgs) => void;
  closeFileViewer: () => void;
};

const FileViewerContext = createContext<FileViewerContextValue | null>(null);

export function useFileViewer(): FileViewerContextValue {
  const ctx = useContext(FileViewerContext);
  if (!ctx) {
    throw new Error('useFileViewer must be used within FileViewerProvider');
  }
  return ctx;
}

/** Safe hook when provider may be absent (returns no-ops). */
export function useFileViewerOptional(): FileViewerContextValue {
  const ctx = useContext(FileViewerContext);
  return (
    ctx ?? {
      openFileViewer: () => undefined,
      closeFileViewer: () => undefined,
    }
  );
}

type LoadedState = {
  blobUrl: string;
  contentType: string;
  fileName: string;
  fileId?: string;
};

function previewKind(contentType: string): 'image' | 'iframe' | 'other' {
  const ct = contentType.toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct.includes('pdf') || ct.includes('html') || ct.startsWith('text/')) return 'iframe';
  return 'other';
}

export function FileViewerProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [siblings, setSiblings] = useState<string[]>([]);
  const [siblingIndex, setSiblingIndex] = useState(0);
  const blobUrlRef = useRef<string | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const revokeBlob = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const closeFileViewer = useCallback(() => {
    setOpened(false);
    setLoading(false);
    setError(null);
    setLoaded(null);
    setSiblings([]);
    setSiblingIndex(0);
    blobRef.current = null;
    revokeBlob();
  }, [revokeBlob]);

  const loadOne = useCallback(
    async (args: { fileId?: string | number; url?: string; fileName?: string }) => {
      setLoading(true);
      setError(null);
      revokeBlob();
      blobRef.current = null;
      setLoaded(null);
      try {
        const result = await fetchFileBlob(args, accessToken);
        const blobUrl = URL.createObjectURL(result.blob);
        blobUrlRef.current = blobUrl;
        blobRef.current = result.blob;
        setLoaded({
          blobUrl,
          contentType: result.contentType,
          fileName: result.fileName,
          fileId: result.fileId,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('common.connectionError'));
      } finally {
        setLoading(false);
      }
    },
    [accessToken, revokeBlob, t],
  );

  const openFileViewer = useCallback(
    (args: FileViewerOpenArgs) => {
      const sibs = (args.siblings ?? [])
        .map((id) => String(id))
        .filter(Boolean);
      let idx = args.siblingIndex ?? 0;
      if (args.fileId != null && sibs.length) {
        const found = sibs.indexOf(String(args.fileId));
        if (found >= 0) idx = found;
      }
      setSiblings(sibs);
      setSiblingIndex(idx);
      setOpened(true);
      void loadOne({
        fileId: args.fileId ?? (sibs[idx] || undefined),
        url: args.url,
        fileName: args.fileName,
      });
    },
    [loadOne],
  );

  useEffect(() => () => revokeBlob(), [revokeBlob]);

  const goSibling = useCallback(
    (delta: number) => {
      if (siblings.length <= 1) return;
      const next = (siblingIndex + delta + siblings.length) % siblings.length;
      setSiblingIndex(next);
      void loadOne({ fileId: siblings[next] });
    },
    [siblings, siblingIndex, loadOne],
  );

  const download = useCallback(() => {
    if (!loaded?.blobUrl) return;
    // Prefer the bytes already stored in the bucket (application/pdf).
    // Do not re-render HTML→canvas in the browser — that produces misaligned PDFs.
    const a = document.createElement('a');
    a.href = loaded.blobUrl;
    a.download = loaded.fileName || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [loaded]);

  const value = useMemo(
    () => ({ openFileViewer, closeFileViewer }),
    [openFileViewer, closeFileViewer],
  );

  const kind = loaded ? previewKind(loaded.contentType) : 'other';

  return (
    <FileViewerContext.Provider value={value}>
      {children}
      <Modal
        opened={opened}
        onClose={closeFileViewer}
        fullScreen
        padding="md"
        withCloseButton={false}
        // Above nested Settings / billing-history modals (Mantine default is 200).
        zIndex={1000}
        title={
          <Group justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
            <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
              {loaded?.fileName || t('common.fileViewer')}
            </Text>
            <Group gap="xs" wrap="nowrap">
              {siblings.length > 1 ? (
                <>
                  <ActionIcon
                    variant="default"
                    aria-label={t('common.previous')}
                    onClick={() => goSibling(-1)}
                    disabled={loading}
                  >
                    <IconChevronLeft size={18} />
                  </ActionIcon>
                  <Text size="xs" c="dimmed">
                    {siblingIndex + 1}/{siblings.length}
                  </Text>
                  <ActionIcon
                    variant="default"
                    aria-label={t('common.next')}
                    onClick={() => goSibling(1)}
                    disabled={loading}
                  >
                    <IconChevronRight size={18} />
                  </ActionIcon>
                </>
              ) : null}
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                onClick={download}
                disabled={!loaded || loading}
              >
                {t('common.download')}
              </Button>
              <ActionIcon
                variant="default"
                aria-label={t('common.close')}
                onClick={closeFileViewer}
              >
                <IconX size={18} />
              </ActionIcon>
            </Group>
          </Group>
        }
      >
        <Stack gap="md" style={{ height: 'calc(100vh - 100px)' }}>
          {loading ? (
            <Stack align="center" justify="center" style={{ flex: 1 }}>
              <Loader />
            </Stack>
          ) : null}
          {error ? <Alert color="red" title={error} /> : null}
          {!loading && !error && loaded ? (
            kind === 'image' ? (
              <img
                src={loaded.blobUrl}
                alt={loaded.fileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  margin: '0 auto',
                  display: 'block',
                }}
              />
            ) : kind === 'iframe' ? (
              <iframe
                title={loaded.fileName}
                src={loaded.blobUrl}
                style={{
                  flex: 1,
                  width: '100%',
                  minHeight: '70vh',
                  border: 'none',
                  borderRadius: 8,
                  background: 'var(--mantine-color-gray-0)',
                }}
              />
            ) : (
              <Stack align="center" justify="center" gap="md" style={{ flex: 1 }}>
                <Text c="dimmed">{t('common.previewUnavailable')}</Text>
                <Button leftSection={<IconDownload size={16} />} onClick={download}>
                  {t('common.download')}
                </Button>
              </Stack>
            )
          ) : null}
        </Stack>
      </Modal>
    </FileViewerContext.Provider>
  );
}

/** Open invoice/order file from a stored `/api/files/:id` (or absolute) URL. */
export function openArgsFromFileUrl(
  url: string,
  fileName?: string,
): FileViewerOpenArgs | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const fileId = parseFileIdFromUrl(trimmed);
  if (fileId) return { fileId, fileName, url: trimmed };
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return { url: trimmed, fileName };
  }
  return null;
}
